"use client"

import { useState, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import Swal from "sweetalert2"
import { useAuth } from "../../../contexts/AuthContext"
import { API_BASE_URL as API_BASE } from "../../../config/api"
import { getAuthToken } from "../../../utils/authStorage"
import { checkCampaignCountryPermissions } from "../../../utils/countryValidation"

const isMetaMediaUrl = (url) => {
  if (!url) return false
  return (
    url.includes(".fbsbx.com") ||
    url.includes("facebook.com") ||
    url.includes("graph.facebook") ||
    url.includes("whatsapp.net") ||
    url.includes("fbcdn.net")
  )
}

function Campaign() {
  const navigate = useNavigate()
  const { currentUser, user } = useAuth()
  const effectiveRole = user?.role || currentUser?.role
  const isRestrictedRole = ["superadmin", "admin", "reseller", "company"].includes(effectiveRole)

  useEffect(() => {
    if (isRestrictedRole) {
      navigate(effectiveRole === "reseller" ? "/dashreseller" : (effectiveRole === "superadmin" ? "/su-admin" : "/dash-admin"), { replace: true })
    }
  }, [isRestrictedRole, effectiveRole, navigate])

  const [loading, setLoading] = useState(true)
  const [templates, setTemplates] = useState([])
  const [filteredTemplates, setFilteredTemplates] = useState([])
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [currentStep, setCurrentStep] = useState(1)
  const [campaignName, setCampaignName] = useState("")
  const [phoneNumbers, setPhoneNumbers] = useState("")
  const [recipientCount, setRecipientCount] = useState(0)
  const [manualVariables, setManualVariables] = useState({})
  const [uploadedMedia, setUploadedMedia] = useState(null)
  const [userBalance, setUserBalance] = useState(0)
  const [balanceValidation, setBalanceValidation] = useState(null)
  const [canProceed, setCanProceed] = useState(false)
  const [campaignId, setCampaignId] = useState(null)
  const [isCreating, setIsCreating] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [campaignSent, setCampaignSent] = useState(false)
  const [sendAttempts, setSendAttempts] = useState(0)
  const [phoneInputMethod, setPhoneInputMethod] = useState("manual")
  const [inputMethod, setInputMethod] = useState("manual")
  const [excelData, setExcelData] = useState([])
  const [campaignReport, setCampaignReport] = useState(null)
  const [showReport, setShowReport] = useState(false)
  const [templateSearchQuery, setTemplateSearchQuery] = useState("")
  const [wabaAccounts, setWabaAccounts] = useState([])
  const [selectedWabaAccount, setSelectedWabaAccount] = useState(null)
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState(null)
  const [showPhoneNumberModal, setShowPhoneNumberModal] = useState(false)
  const [loadingWaba, setLoadingWaba] = useState(false)
  const [variableMapping, setVariableMapping] = useState({})
  const [excelColumns, setExcelColumns] = useState([])
  const [showColumnMapping, setShowColumnMapping] = useState(false)
  const [uploadedVariableFile, setUploadedVariableFile] = useState(null)
  const [savedMappings, setSavedMappings] = useState([])
  const [phoneColumn, setPhoneColumn] = useState("")
  const [mediaUrl, setMediaUrl] = useState("")
  const [totalRecords, setTotalRecords] = useState(0)
  const [excelMappingConfigured, setExcelMappingConfigured] = useState(false)
  const [campaignCreated, setCampaignCreated] = useState(false)
  const [currentLimit, setCurrentLimit] = useState(null)
  const [limitResetOn, setlimitResetOn] = useState(null)
  const [maxLimit, setMaxLimit] = useState(null)
  const [loadingLimit, setLoadingLimit] = useState(false)

  // Karix helper states
  const [karixTemplateId, setKarixTemplateId] = useState("")
  const [karixParamCount, setKarixParamCount] = useState(0)
  const [karixLanguage, setKarixLanguage] = useState("en")

  const [isPolling, setIsPolling] = useState(false)
  const [pollingInterval, setPollingInterval] = useState(null)
  const [scheduleOption, setScheduleOption] = useState("immediate")
  const [scheduleTime, setScheduleTime] = useState(() => {
    const d = new Date()
    d.setHours(d.getHours() + 1)
    const offsetMs = d.getTimezoneOffset() * 60 * 1000
    const localDate = new Date(d.getTime() - offsetMs)
    return localDate.toISOString().slice(0, 16)
  })

  const handlePhoneNumberSelect = (phoneNumber) => {
    setSelectedPhoneNumber(phoneNumber)
    setShowPhoneNumberModal(false)
  }

  const getPhoneNumberCount = (account) => {
    return account.phoneNumbers ? account.phoneNumbers.length : 0
  }

  const getHeaderMediaType = () => {
    if (!selectedTemplate) return null
    const analysisType = selectedTemplate.parameterAnalysis?.headerType?.toLowerCase()
    if (["image", "video", "document"].includes(analysisType)) {
      return analysisType
    }
    if (selectedTemplate.components) {
      const headerComp = selectedTemplate.components.find((c) => c.type === "HEADER")
      const format = headerComp?.format?.toLowerCase()
      if (["image", "video", "document"].includes(format)) {
        return format
      }
    }
    return null
  }

  useEffect(() => {
    loadTemplates()
    loadUserBalance()
    loadWabaAccounts()
  }, [])

  // Start of updated polling effect
  useEffect(() => {
    let pollInterval

    if (campaignSent && campaignId) {
      // Poll for campaign status every 3 seconds
      pollInterval = setInterval(async () => {
        try {
          const response = await fetch(`${API_BASE}/api/campaigns/${campaignId}`, {
            headers: {
              Authorization: `Bearer ${getAuthToken()}`,
            },
          })

          if (response.ok) {
            const data = await response.json()

            // Update campaign report with latest stats
            setCampaignReport((prev) => ({
              ...prev,
              messagesSent: data.campaign.queueData?.messagesSent || 0,
              queueStatus: data.campaign.queueData?.queueStatus || "processing",
              stats: data.campaign.stats || {}, // Update stats from response
              progress: data.campaign.progress || 0, // Update progress
            }))

            // If campaign is completed, stop polling and redirect
            if (data.campaign.status === "completed") {
              clearInterval(pollInterval)
              setIsPolling(false) // Ensure polling state is false

              // Show final success message
              if (typeof window !== "undefined" && window.Swal) {
                window.Swal.fire({
                  icon: "success",
                  title: "Campaign Completed Successfully!",
                  html: `
                    <div class="text-left">
                      <p><strong>Messages Sent:</strong> ${data.campaign.queueData?.messagesSent}</p>
                      <p><strong>Failed:</strong> ${data.campaign.stats?.failed || 0}</p>
                      <p><strong>Total:</strong> ${data.campaign.stats?.total || 0}</p>
                    </div>
                  `,
                  confirmButtonColor: "#10b981",
                  timer: 5000, // Display for 5 seconds
                  timerProgressBar: true,
                }).then(() => {
                  // Redirect to report page using window.location
                  window.location.href = `/campaign-report`
                })
              } else {
                // Fallback for environments without Swal
                Swal.fire({
                  icon: "success",
                  title: "Campaign Completed!",
                  text: `${data.campaign.queueData?.messagesSent} messages sent.`,
                  confirmButtonText: "View Report",
                }).then(() => {
                  window.location.href = "/campaign-report"
                })

              }
            }
          } else {
            console.error("Failed to fetch campaign status:", response.status, await response.text())
          }
        } catch (error) {
          console.error("Error polling campaign status:", error)
          // Optionally clear interval on persistent error
          // clearInterval(pollInterval);
          // setIsPolling(false);
        }
      }, 3000) // Poll every 3 seconds
    }

    // Cleanup function to clear interval when component unmounts or campaignSent/campaignId changes
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval)
      }
    }
  }, [campaignSent, campaignId]) // Dependencies for this effect
  // End of updated polling effect

  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval)
      }
    }
  }, [pollingInterval])

  useEffect(() => {
    if (phoneNumbers) {
      const numbers = phoneNumbers.split("\n").filter((num) => num.trim().length > 0)
      setRecipientCount(numbers.length)
    } else {
      setRecipientCount(0)
    }
  }, [phoneNumbers])

  useEffect(() => {
    validateBalance()
  }, [recipientCount, userBalance])

  useEffect(() => {
    if (templateSearchQuery.trim() === "") {
      setFilteredTemplates(templates)
    } else {
      const filtered = templates.filter(
        (template) =>
          template.name.toLowerCase().includes(templateSearchQuery.toLowerCase()) ||
          template.category.toLowerCase().includes(templateSearchQuery.toLowerCase()),
      )
      setFilteredTemplates(filtered)
    }
  }, [templateSearchQuery, templates])

  //   useEffect(() => {
  //     if (selectedWabaAccount && selectedWabaAccount.phoneNumbers && selectedWabaAccount.phoneNumbers.length > 0) {
  //       if (!selectedPhoneNumber) {
  //         setSelectedPhoneNumber(selectedWabaAccount.phoneNumbers[0])
  //       }
  //     }
  //   }, [selectedWabaAccount])

  useEffect(() => {
    if (selectedWabaAccount && selectedWabaAccount.phoneNumbers && selectedWabaAccount.phoneNumbers.length > 0) {
      // For Pinacle operator, auto-select the default phone number
      if (user?.operator === "pinacle") {
        const defaultPhone = selectedWabaAccount.phoneNumbers.find((p) => p.default === true)
        if (defaultPhone) {
          setSelectedPhoneNumber(defaultPhone)
        } else if (!selectedPhoneNumber) {
          // Fallback to first phone if no default is set
          setSelectedPhoneNumber(selectedWabaAccount.phoneNumbers[0])
        }
      } else if (!selectedPhoneNumber) {
        setSelectedPhoneNumber(selectedWabaAccount.phoneNumbers[0])
      }
    }
  }, [selectedWabaAccount, user?.operator])


  // ✅ Automatically switch to Excel mode in Step 3 when mapping is ready
  useEffect(() => {
    const excelReady = excelMappingConfigured || (Array.isArray(excelData) && excelData.length > 0);
    if (currentStep === 3 && excelReady) {
      setPhoneInputMethod("excel");
      setInputMethod("excel");
    }
  }, [currentStep, excelMappingConfigured, excelData]);

  const availableSenderNumbers = useMemo(() => {
    const list = []
    const addedIds = new Set()

    // 1. First, add the user's primary/default number if configured
    if (user?.phoneNumberId || user?.cloudNumber || user?.number) {
      const pid = user.phoneNumberId || user.cloudNumber || user.number
      list.push({
        id: pid,
        phoneNumberId: pid,
        display_phone_number: user.cloudNumber || user.number || pid,
        verified_name: user.label || user.company || "Primary Number",
        quality_rating: "GREEN",
        status: "CONNECTED",
        wabaId: user.wabaId || user.businessAccountId || "",
        token: user.token || "",
        isPrimary: true,
      })
      addedIds.add(pid)
    }

    // 2. Add numbers from user.wabaNumbers array (from User Accounts / Cloud DB)
    if (user?.wabaNumbers && Array.isArray(user.wabaNumbers)) {
      user.wabaNumbers.forEach((wn, idx) => {
        const pid = wn.phoneNumberId || wn.cloudNumber
        if (pid && !addedIds.has(pid)) {
          list.push({
            id: pid,
            phoneNumberId: pid,
            display_phone_number: wn.cloudNumber || wn.phoneNumberId || pid,
            verified_name: wn.label || `Sender #${idx + 1}`,
            quality_rating: "GREEN",
            status: "CONNECTED",
            wabaId: wn.wabaId || user?.wabaId || "",
            token: wn.token || user?.token || "",
            isPrimary: false,
          })
          addedIds.add(pid)
        }
      })
    }

    // 3. Add numbers from wabaAccounts (from Meta API / sync)
    if (wabaAccounts && Array.isArray(wabaAccounts)) {
      wabaAccounts.forEach((acc) => {
        if (acc.phoneNumbers && Array.isArray(acc.phoneNumbers)) {
          acc.phoneNumbers.forEach((p) => {
            const pid = p.id || p.phoneNumberId || p.phone_number_id
            if (pid && !addedIds.has(pid)) {
              list.push({
                ...p,
                id: pid,
                phoneNumberId: pid,
                display_phone_number: p.display_phone_number || p.displayPhoneNumber || pid,
                verified_name: p.verified_name || p.verifiedName || acc.name || "WhatsApp Sender",
                wabaId: acc.id || acc.wabaId || "",
                isPrimary: false,
              })
              addedIds.add(pid)
            }
          })
        }
      })
    }

    return list
  }, [user, wabaAccounts])

  useEffect(() => {
    if (!selectedPhoneNumber && availableSenderNumbers.length > 0) {
      setSelectedPhoneNumber(availableSenderNumbers[0])
    }
  }, [availableSenderNumbers, selectedPhoneNumber])

  const handleSenderNumberConfirmSelect = async (targetPhone) => {
    if (selectedPhoneNumber && (selectedPhoneNumber.id === targetPhone.id || selectedPhoneNumber.phoneNumberId === targetPhone.phoneNumberId || selectedPhoneNumber.display_phone_number === targetPhone.display_phone_number)) {
      return // already selected
    }

    const labelName = targetPhone.verified_name || targetPhone.display_phone_number || targetPhone.id

    const result = await Swal.fire({
      title: "Change Sender Number?",
      text: `Are you sure you want to select "${labelName}" (${targetPhone.display_phone_number}) for sending this campaign?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#4A7C59",
      cancelButtonColor: "#6B7280",
      confirmButtonText: "Yes, Select",
    })

    if (result.isConfirmed) {
      setSelectedPhoneNumber(targetPhone)
      if (targetPhone.wabaId && wabaAccounts) {
        const matchingAcc = wabaAccounts.find((a) => (a.id || a.wabaId) === targetPhone.wabaId)
        if (matchingAcc) {
          setSelectedWabaAccount(matchingAcc)
        }
      }
      Swal.fire({
        icon: "success",
        title: "Sender Number Changed",
        text: `Campaign will now be sent using ${targetPhone.display_phone_number}.`,
        confirmButtonColor: "#4A7C59",
        timer: 1500,
        showConfirmButton: false,
      })
    }
  }


  const canProceedToNextStep = () => {

    if (currentStep === 1) {

      return selectedTemplate !== null && selectedPhoneNumber !== null

    }

    if (currentStep === 2) {


      if (!campaignName.trim()) return false

      if (!campaignCreated) return false



      if (!campaignCreated) return false



      const analysis = selectedTemplate?.parameterAnalysis || {}

      const bodyParameterCount = analysis.bodyParameterCount || 0



      if (inputMethod === "excel" && bodyParameterCount > 0) {

        // Require mapping + phone column for Excel

        return excelMappingConfigured && phoneColumn

      }

      return true

    }

    if (currentStep === 3) {

      // ✅ Allow sending if recipients exist from Excel mapping OR manual

      return recipientCount > 0 && canProceed

    }

    return true

  }

  const loadSenderLimit = async (wabaAccountId) => {
    if (!wabaAccountId) return;
    try {
      setLoadingLimit(true);
      const response = await fetch(`${API_BASE}/api/campaigns/sender-limit/${wabaAccountId}`, {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentLimit(data.currentLimit);
        setlimitResetOn(data.limitResetOn);
        setMaxLimit(data.maxLimit);
      }
    } catch (error) {
      console.error("Error loading sender limit:", error);
    } finally {
      setLoadingLimit(false);
    }
  };

  useEffect(() => {
    if (selectedWabaAccount?._id || selectedWabaAccount?.id) {
      loadSenderLimit(selectedWabaAccount._id || selectedWabaAccount.id);
    }
  }, [selectedWabaAccount]);

  const loadWabaAccounts = async () => {
    try {
      setLoadingWaba(true)
      const response = await fetch(`${API_BASE}/api/waba/accounts`, {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        setWabaAccounts(data.accounts || [])

        // Auto-select first account if only one exists
        if (data.accounts && data.accounts.length === 1) {
          setSelectedWabaAccount(data.accounts[0])
        } else if (data.accounts && data.accounts.length > 1) {
          // Default select first account
          setSelectedWabaAccount(data.accounts[0])
        }
      }
    } catch (error) {
      console.error("Error loading WABA accounts:", error)
    } finally {
      setLoadingWaba(false)
    }
  }

  const loadTemplates = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/campaigns/templates`, {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      })
      const data = await response.json()
      if (data.success && data.templates) {
        setTemplates(data.templates)
        setFilteredTemplates(data.templates)
      }
    } catch (error) {
      console.error("Error loading templates:", error)
    } finally {
      setLoading(false)
    }
  }

  const loadUserBalance = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/user/balance`, {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          "Content-Type": "application/json",
        },
      })

      if (response.ok) {
        const data = await response.json()
        setUserBalance(data.balance || 0)
      }
    } catch (error) {
      console.error("Error loading user balance:", error)
    }
  }

  const validateBalance = async () => {
    if (recipientCount === 0) {
      setBalanceValidation(null)
      setCanProceed(false)
      return
    }

    const costPerMessage = 1
    const totalCost = recipientCount * costPerMessage

    if (userBalance >= totalCost) {
      setBalanceValidation({
        status: "success",
        message: `✓ Sufficient balance.`,
        totalCost,
      })
      setCanProceed(true)
    } else {
      setBalanceValidation({
        status: "error",
        message: `✗ Insufficient balance. Available: ₹${userBalance.toFixed(2)}`,
        totalCost,
      })
      setCanProceed(false)
    }
  }

  const createCampaign = async () => {
    console.log("createCampaign 1")
    if (campaignId && campaignId !== "null" && campaignId !== null) {
      console.log("Campaign already exists with ID:", campaignId)
      setCampaignCreated(true)
      return campaignId
    }

    const analysis = selectedTemplate?.parameterAnalysis || {}

    let bodyVariables = []

    if (inputMethod === "manual") {
      bodyVariables = Object.keys(manualVariables)
        .filter((key) => manualVariables[key] && manualVariables[key].trim() !== "")
        .map((key) => ({
          index: Number.parseInt(key),
          name: `Variable ${Number.parseInt(key) + 1}`,
          type: "text",
          defaultValue: manualVariables[key].trim(),
        }))
    } else if (inputMethod === "excel") {
      bodyVariables = []
    }

    let headerContentData = uploadedMedia
    const mediaType = analysis.headerType || getHeaderMediaType()

    if (user?.operator === "karix" && uploadedMedia && mediaType) {
      headerContentData = {
        type: mediaType.toLowerCase(),
        url: uploadedMedia.mediaUrl || uploadedMedia.whatsappUrl,
        ...(mediaType.toLowerCase() === "document" && uploadedMedia.fileName
          ? { fileName: uploadedMedia.fileName }
          : {}),
      }
    }

    const templateCategory = (selectedTemplate.category || "").toString().trim().toLowerCase()
    const validMessageTypes = ["marketing", "utility", "authentication"]
    const campaignMessageType = validMessageTypes.includes(templateCategory)
      ? templateCategory
      : "marketing"

    const campaignData = {
      campaignName: campaignName.trim(),
      templateId: selectedTemplate.id,
      templateName: selectedTemplate.name,
      templateData: selectedTemplate,
      language: selectedTemplate.language || "en",
      headerType: analysis.headerType || (getHeaderMediaType() ? getHeaderMediaType().toUpperCase() : undefined),
      headerContent: headerContentData, // Send dynamic headerContent
      bodyVariables,
      mediaId: ["cloud", "pinacle"].includes(user?.operator) ? uploadedMedia?.mediaId : null,
      mediaUrl: uploadedMedia?.mediaUrl || uploadedMedia?.whatsappUrl || null,
      messageType: campaignMessageType,
      wabaAccountId: selectedWabaAccount?.id,
      inputMethod,
      karixSenderFrom: undefined,
      karixWebhookDNId: undefined,
    }

    try {
      setIsCreating(true)
      const response = await fetch(`${API_BASE}/api/campaigns/create`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(campaignData),
      })

      const data = await response.json()
      console.log("Campaign creation response:", data)

      if (response.ok && data) {
        const newCampaignId = data.campaign?._id || data._id || data.id || data.campaignId || null

        if (newCampaignId && newCampaignId !== "null") {
          console.log("Setting campaign ID:", newCampaignId)
          setCampaignId(newCampaignId)
          setCampaignCreated(true)
          console.log("createCampaign 2")
          return newCampaignId
        } else {
          console.error("No valid campaign ID in response:", data)
          throw new Error("Failed to create campaign - no campaign ID returned")
        }
      } else {
        throw new Error(data.error || data.message || "Failed to create campaign")
      }
    } catch (error) {
      console.error("Campaign creation error:", error)
      alert(`Error creating campaign: ${error.message}`)
      return false
    } finally {
      setIsCreating(false)
    }
  }
  const uploadPhoneNumbers = async (targetCampaignId = campaignId) => {
    if (!targetCampaignId || targetCampaignId === "null" || !phoneNumbers.trim()) return false

    try {
      const numbers = phoneNumbers.split("\n").filter((num) => num.trim().length > 0)
      const csvContent = "phone\n" + numbers.join("\n")
      const blob = new Blob([csvContent], { type: "text/csv" })
      const file = new File([blob], "phone_numbers.csv", { type: "text/csv" })

      const formData = new FormData()
      formData.append("excel", file)

      const response = await fetch(`${API_BASE}/api/campaigns/${targetCampaignId}/upload-numbers`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: formData,
      })

      const data = await response.json()
      return response.ok && data.success
    } catch (error) {
      console.error("Error uploading phone numbers:", error)
      return false
    }
  }

  const handleMediaUpload = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    const previewUrl = URL.createObjectURL(file)

    const formData = new FormData()
    formData.append("media", file)

    try {
      const response = await fetch(`${API_BASE}/api/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: formData,
      })

      const data = await response.json()

      if (data.success) {
        setUploadedMedia({
          mediaId: data.data.mediaId, // Cloud uses this
          whatsappUrl: data.data.whatsappUrl || data.data.mediaUrl || "", // Both operators
          mediaUrl: data.data.mediaUrl || data.data.whatsappUrl || "", // Karix needs this
          localPreviewUrl: previewUrl,
          fileName: file.name,
          fileType: data.data.fileType || file.type,
        })

        // SweetAlert success
        if (typeof window !== "undefined" && window.Swal) {
          window.Swal.fire({
            icon: "success",
            title: "Media Uploaded Successfully!",
            text: `Media ID: ${data.data.mediaId}`,
            confirmButtonColor: "#10b981",
          })
        }
      } else {
        throw new Error(data.error || "Upload failed")
      }
    } catch (error) {
      console.error("Media upload error:", error)
      if (typeof window !== "undefined" && window.Swal) {
        window.Swal.fire({
          icon: "error",
          title: "Upload Failed",
          text: `Error uploading media: ${error.message}`,
          confirmButtonColor: "#ef4444",
        })
      }
    }
  }

  const handlePhoneExcelUpload = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    let targetCampaignId = campaignId

    if (!targetCampaignId || targetCampaignId === "null") {
      const created = await createCampaign()
      if (!created) {
        if (typeof window !== "undefined" && window.Swal) {
          window.Swal.fire({
            icon: "error",
            title: "Campaign Creation Failed",
            text: "Please create campaign first before uploading Excel.",
            confirmButtonColor: "#ef4444",
          })
        }
        return
      }
      targetCampaignId = created
    }

    const formData = new FormData()
    formData.append("excel", file)

    try {
      const response = await fetch(`${API_BASE}/api/campaigns/${targetCampaignId}/upload-numbers`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: formData,
      })

      const data = await response.json()

      if (data.success) {
        setExcelData(data.data || [])
        setRecipientCount(data.validNumbers || 0)
        if (typeof window !== "undefined" && window.Swal) {
          window.Swal.fire({
            icon: "success",
            title: "Phone Numbers Uploaded!",
            text: `Valid numbers: ${data.validNumbers || 0}`,
            confirmButtonColor: "#10b981",
          })
        }
      } else {
        throw new Error(data.error || "Upload failed")
      }
    } catch (error) {
      console.error("Phone Excel upload error:", error)
      if (typeof window !== "undefined" && window.Swal) {
        window.Swal.fire({
          icon: "error",
          title: "Upload Failed",
          text: `Error uploading phone Excel: ${error.message}`,
          confirmButtonColor: "#ef4444",
        })
      }
    }
  }

  const handleVariableExcelUpload = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    if (!campaignName.trim()) {
      if (typeof window !== "undefined" && window.Swal) {
        window.Swal.fire({
          icon: "error",
          title: "Campaign Name Required",
          text: "Please enter a campaign name before uploading Excel.",
          confirmButtonColor: "#ef4444",
        })
      }
      return
    }

    let idToUse = campaignId

    // If no campaignId yet, create one first
    if (!idToUse || idToUse === "null") {
      console.log("[v0] Creating campaign before variable upload...")
      const created = await createCampaign()
      if (!created) {
        console.log("[v0] Campaign creation failed")
        if (typeof window !== "undefined" && window.Swal) {
          window.Swal.fire({
            icon: "error",
            title: "Campaign Creation Failed",
            text: "Please create campaign first before uploading Excel.",
            confirmButtonColor: "#ef4444",
          })
        }
        return
      }

      idToUse = created
    }

    console.log("[v0] Using campaign ID for variable upload:", idToUse)

    setUploadedVariableFile(file.name)
    const formData = new FormData()
    formData.append("excel", file)

    try {
      const response = await fetch(`${API_BASE}/api/campaigns/${idToUse}/upload-variables`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: formData,
      })

      const data = await response.json()
      console.log("[v0] Variable upload response:", data)

      if (data.success) {
        setExcelColumns(data.availableColumns || data.columns || [])
        setPhoneColumn(data.phoneColumn || "")
        setTotalRecords(data.totalRecords || 0)
        setExcelData(data.sampleData || [])
        setShowColumnMapping(true)
        setExcelMappingConfigured(false)

        if (typeof window !== "undefined" && window.Swal) {
          window.Swal.fire({
            icon: "success",
            title: "Excel Uploaded!",
            text: `Found ${data.availableColumns?.length || 0} variable columns. Please configure variable mapping.`,
            confirmButtonColor: "#10b981",
          })
        }
      } else {
        throw new Error(data.error || "Upload failed")
      }
    } catch (error) {
      console.error("Variable Excel upload error:", error)
      if (typeof window !== "undefined" && window.Swal) {
        window.Swal.fire({
          icon: "error",
          title: "Upload Failed",
          text: `Error uploading Excel: ${error.message}`,
          confirmButtonColor: "#ef4444",
        })
      }
    }
  }

  const handleSaveColumnMapping = async () => {
    if (!campaignId || campaignId === "null") {
      if (typeof window !== "undefined" && window.Swal) {
        window.Swal.fire({
          icon: "error",
          title: "No Campaign Found",
          text: "Please create a campaign first.",
          confirmButtonColor: "#ef4444",
        })
      }
      return
    }

    if (!phoneColumn) {
      if (typeof window !== "undefined" && window.Swal) {
        window.Swal.fire({
          icon: "error",
          title: "Phone Column Required",
          text: "Please select a phone column for mapping.",
          confirmButtonColor: "#ef4444",
        })
      }
      return
    }

    // Create complete mapping object including phone column
    const completeMapping = {
      phone: phoneColumn,
      ...variableMapping,
    }

    try {
      console.log("[v0] Saving column mapping for campaign:", campaignId)
      console.log("[v0] Complete mapping:", completeMapping)

      const response = await fetch(`${API_BASE}/api/campaigns/${campaignId}/save-mapping`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({
          columnMapping: completeMapping,
        }),
      })

      const data = await response.json()
      console.log("[v0] Save mapping response:", data)

      if (data.success) {
        const mappingData = {
          templateId: selectedTemplate.id,
          templateName: selectedTemplate.name,
          mapping: completeMapping,
          savedAt: new Date().toISOString(),
        }

        // Save to local storage for future campaigns
        const existingMappings = JSON.parse(localStorage.getItem("variableMappings") || "[]")
        const updatedMappings = existingMappings.filter((m) => m.templateId !== selectedTemplate.id)
        updatedMappings.push(mappingData)
        localStorage.setItem("variableMappings", JSON.stringify(updatedMappings))
        setSavedMappings(updatedMappings)

        setShowColumnMapping(false)
        setExcelMappingConfigured(true)
        setRecipientCount(data.phoneCount || 0)

        if (typeof window !== "undefined" && window.Swal) {
          window.Swal.fire({
            icon: "success",
            title: "Mapping Saved!",
            text: `Variable mapping saved successfully. ${data.phoneCount || 0} recipients processed.`,
            confirmButtonColor: "#10b981",
          })
        }
      } else {
        throw new Error(data.error || "Failed to save mapping")
      }
    } catch (error) {
      console.error("Save mapping error:", error)
      if (typeof window !== "undefined" && window.Swal) {
        window.Swal.fire({
          icon: "error",
          title: "Save Failed",
          text: `Error saving mapping: ${error.message}`,
          confirmButtonColor: "#ef4444",
        })
      }
    }
  }

  const loadSavedMappings = () => {
    const mappings = JSON.parse(localStorage.getItem("variableMappings") || "[]")
    setSavedMappings(mappings)

    // Auto-load mapping if exists for current template
    if (selectedTemplate) {
      const templateMapping = mappings.find((m) => m.templateId === selectedTemplate.id)
      if (templateMapping) {
        setVariableMapping(templateMapping.mapping)
      }
    }
  }

  useEffect(() => {
    if (selectedTemplate) {
      loadSavedMappings()
    }
  }, [selectedTemplate])

  const saveManualVariables = async (targetCampaignId = campaignId) => {
    if (!targetCampaignId || targetCampaignId === "null") {
      if (typeof window !== "undefined" && window.Swal) {
        window.Swal.fire({
          icon: "error",
          title: "Create campaign first",
          text: "Please create a campaign before saving variables.",
          confirmButtonColor: "#ef4444",
        })
      }
      return false
    }

    // Build bodyVariables from current manualVariables state, only non-empty values
    const bodyVariables = Object.keys(manualVariables || {})
      .filter((key) => manualVariables[key] && manualVariables[key].trim() !== "")
      .map((key) => ({
        index: Number.parseInt(key),
        name: `Variable ${Number.parseInt(key) + 1}`,
        type: "text",
        defaultValue: manualVariables[key].trim(),
      }))

    try {
      const response = await fetch(`${API_BASE}/api/campaigns/${targetCampaignId}/variables`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          bodyVariables,
          source: "manual",
        }),
      })

      const data = await response.json()
      if (!response.ok) throw new Error(data.error || data.message || "Failed to save variables")

      if (typeof window !== "undefined" && window.Swal) {
        window.Swal.fire({
          icon: "success",
          title: "Variables saved",
          text: "Manual variables have been saved to this campaign.",
          confirmButtonColor: "#10b981",
        })
      }
      return true
    } catch (err) {
      console.error("saveManualVariables error:", err)
      if (typeof window !== "undefined" && window.Swal) {
        window.Swal.fire({
          icon: "error",
          title: "Save failed",
          text: err.message,
          confirmButtonColor: "#ef4444",
        })
      }
      return false
    }
  }

  const pollCampaignStatus = async (campaignId) => {
    try {
      const response = await fetch(`${API_BASE}/api/campaigns/${campaignId}/status`, {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      })

      const data = await response.json()

      if (data.success) {
        // Update campaign report with latest stats
        setCampaignReport((prev) => ({
          ...prev,
          messagesSent: data.stats.sent || 0,
          queueStatus: data.status === "completed" ? "completed" : "processing",
          progress: data.progress || 0,
          stats: data.stats,
        }))

        // If campaign is completed, stop polling and redirect
        if (data.status === "completed") {
          setIsPolling(false)
          if (pollingInterval) {
            clearInterval(pollingInterval)
            setPollingInterval(null)
          }

          // Show success message
          if (typeof window !== "undefined" && window.Swal) {
            window.Swal.fire({
              icon: "success",
              title: "Campaign Completed Successfully!",
              html: `
                <div class="text-left">
                  <p><strong>Messages Sent:</strong> ${data.stats.sent || 0}</p>
                  <p><strong>Failed:</strong> ${data.stats.failed || 0}</p>
                  <p><strong>Total:</strong> ${data.stats.total || 0}</p>
                </div>
              `,
              confirmButtonColor: "#10b981",
              timer: 3000,
              timerProgressBar: true,
            }).then(() => {
              // Redirect to report page
              // router.push(`/reports?campaignId=${campaignId}`) // <-- Router removed
            })
          } else {
            // Fallback redirect if Swal is not available
            setTimeout(() => {
              // router.push(`/reports?campaignId=${campaignId}`) // <-- Router removed
            }, 2000)
          }
        }
      }
    } catch (error) {
      console.error("Error polling campaign status:", error)
    }
  }

  const startStatusPolling = (campaignId) => {
    setIsPolling(true)

    // Poll immediately
    pollCampaignStatus(campaignId)

    // Then poll every 3 seconds
    const interval = setInterval(() => {
      pollCampaignStatus(campaignId)
    }, 3000)

    setPollingInterval(interval)
  }

  async function sendCampaign() {
    let targetCampaignId = campaignId;
    if (!targetCampaignId || targetCampaignId === "null") {
      targetCampaignId = await createCampaign();
      if (!targetCampaignId) {
        if (typeof window !== "undefined" && window.Swal) {
          window.Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to create campaign. Please try again.",
            confirmButtonColor: "#10b981",
          });
        }
        return;
      }
    }

    // Prevent multiple clicks
    if (isSending || isCreating || campaignSent) {
      return
    }

    if (scheduleOption === "later") {
      const selectedDate = new Date(scheduleTime)
      if (selectedDate <= new Date()) {
        if (typeof window !== "undefined" && window.Swal) {
          window.Swal.fire({
            icon: "warning",
            title: "Invalid schedule time",
            text: "Please select a date and time in the future.",
            confirmButtonColor: "#10b981",
          })
        }
        return
      }
    }

    const analysis = selectedTemplate?.parameterAnalysis || {}
    const headerMediaType = getHeaderMediaType()
    const headerIsMedia = !!headerMediaType
    if (headerIsMedia && !uploadedMedia) {
      if (typeof window !== "undefined" && window.Swal) {
        window.Swal.fire({
          icon: "warning",
          title: "Media required",
          text: "This template requires a media header (image/video/document). Please upload the file before sending.",
          confirmButtonColor: "#10b981",
        })
      }
      return
    }

    let autoScheduleSpillover = false;

    console.log("[sendCampaign] Debug Limit:", { currentLimit, maxLimit, recipientCount });

    // Check limit
    if (currentLimit !== null && maxLimit !== Infinity && recipientCount > currentLimit) {
      const resetText = limitResetOn ? `<br/> Your current limit may be refreshed after <b>${limitResetOn}</b>` : "";
      
      const result = await Swal.fire({
        title: "Messaging Limit Exceeded",
        html: `Your current available WhatsApp limit is <b>${currentLimit} messages</b> today, but you are trying to send <b>${recipientCount} Messages.</b> ${resetText}`,
        icon: "error",
      });

      return;
    } else {
      // 🌍 Pre-validation for International Country Permissions
      let numbersToCheck = []
      if (inputMethod === "manual" || phoneInputMethod === "manual") {
        if (typeof phoneNumbers === "string") {
          numbersToCheck = phoneNumbers.split("\n").filter((n) => n && n.trim().length > 0)
        }
      } else if (excelData && Array.isArray(excelData) && excelData.length > 0) {
        numbersToCheck = excelData
      }

      if (numbersToCheck.length > 0) {
        const canProceedCountry = await checkCampaignCountryPermissions(numbersToCheck, user, Swal)
        if (!canProceedCountry) {
          return
        }
      }

      // Show normal confirmation dialog if no spillover logic triggered
      const isScheduling = scheduleOption === "later"
      const titleText = isScheduling
        ? "Are you sure you want to send later?"
        : "Are you sure you want to send immediately?"
      const bodyText = isScheduling
        ? `This campaign will be scheduled for ${new Date(scheduleTime).toLocaleString()}.`
        : `This campaign will be sent to ${recipientCount} recipients immediately.`
      const confirmButtonText = isScheduling
        ? "Yes, Schedule Later!"
        : "Yes, Send Immediately!"

      const result = await Swal.fire({
        title: titleText,
        text: bodyText,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#10b981",
        cancelButtonColor: "#6b7280",
        confirmButtonText: confirmButtonText,
        cancelButtonText: "Cancel",
      })

      if (!result.isConfirmed) {
        return
      }
    }

    try {
      setIsSending(true)
      setCampaignSent(false)
      // Campaign creation is now handled at the start of the function

      if (
        (inputMethod === "manual" || phoneInputMethod === "manual") &&
        manualVariables &&
        Object.keys(manualVariables).length > 0
      ) {
        await saveManualVariables(targetCampaignId)
      }

      // Only upload phone numbers for manual input method
      // Only upload numbers for manual input method
      if (inputMethod === "manual" || phoneInputMethod === "manual") {
        const numbersUploaded = await uploadPhoneNumbers(targetCampaignId)
        if (!numbersUploaded) {
          setIsSending(false)
          if (typeof window !== "undefined" && window.Swal) {
            window.Swal.fire({
              icon: "error",
              title: "Failed to upload phone numbers",
              text: "Please check your phone numbers and try again.",
              confirmButtonColor: "#ef4444",
            })
          }
          return
        }
      }
      // ✅ If Excel mapping, do nothing – numbers already inserted in backend
      const sendResponse = await fetch(`${API_BASE}/api/campaigns/${targetCampaignId}/send`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          wabaAccountId: selectedWabaAccount?.id,
          phoneNumberId: selectedPhoneNumber?.id,
          sendVia: "panel",
          operator: user?.operator || "cloud",
          inputMethod: inputMethod, // Send the input method to backend
          useExcelData: inputMethod === "excel", // Indicate if using Excel data
          mediaId: uploadedMedia?.mediaId || null,
          mediaUrl: uploadedMedia?.whatsappUrl || null,
          autoScheduleSpillover: autoScheduleSpillover,
          // also send headerContent when header is text
          headerContent:
            analysis?.headerType?.toLowerCase() === "text" && uploadedMedia ? String(uploadedMedia) : undefined,
          scheduledAt: scheduleOption === "later" ? new Date(scheduleTime).toISOString() : undefined,
        }),
      })

      const sendData = await sendResponse.json()

      if (sendData.success) {
        setUserBalance(sendData.remainingBalance || 0)
        setCampaignSent(true)
        setSendAttempts((prev) => prev + 1)

        if (sendData.status === "scheduled") {
          setCampaignReport({
            messagesSent: 0,
            queueStatus: "scheduled",
            campaignId: targetCampaignId,
            timestamp: new Date().toISOString(),
            scheduledAt: sendData.scheduledAt,
            operator: user?.operator || "cloud",
            sendVia: "panel",
            status: "scheduled",
            inputMethod: inputMethod,
            progress: 0,
            stats: {
              total: recipientCount,
              sent: 0,
              failed: 0,
            },
          })
          setShowReport(true)

          Swal.fire({
            icon: "success",
            title: "Campaign Created Successfully!",
            html: `
            <div class="text-left">
              <p><strong>Total Recipients:</strong> ${recipientCount}</p>
              <p><strong>Scheduled Time:</strong> ${new Date(sendData.scheduledAt).toLocaleString()}</p>
              <p><strong>Campaign ID:</strong> ${targetCampaignId?.slice(-8)}</p>
              <p><strong>Remaining Balance:</strong> ₹${(sendData.remainingBalance || 0).toFixed(2)}</p>
              <p class="mt-2 text-sm text-gray-600">The campaign has been scheduled and will be sent automatically.</p>
            </div>
          `,
            confirmButtonColor: "#10b981",
            timer: 3000,
            timerProgressBar: true,
          }).then(() => {
            window.location.href = "/campaign-report"
          })
        } else {
          setCampaignReport({
            messagesSent: 0, // Start at 0, will be updated by polling
            queueStatus: "processing",
            campaignId: targetCampaignId,
            timestamp: new Date().toISOString(),
            operator: user?.operator || "cloud",
            sendVia: "panel",
            status: "processing",
            inputMethod: inputMethod,
            progress: 0,
            stats: {
              total: sendData.totalMessages || 0,
              sent: 0,
              failed: 0,
            },
          })
          setShowReport(true)

          Swal.fire({
            icon: "success",
            title: "Campaign Created Successfully!",
            html: `
            <div class="text-left">
              <p><strong>Total Recipients:</strong> ${sendData.totalMessages || 0}</p>
              <p><strong>Campaign ID:</strong> ${targetCampaignId?.slice(-8)}</p>
              <p><strong>Remaining Balance:</strong> ₹${(sendData.remainingBalance || 0).toFixed(2)}</p>
              <p class="mt-2 text-sm text-gray-600">The campaign was created and processing has started.</p>
            </div>
          `,
            confirmButtonColor: "#10b981",
            timer: 3000,
            timerProgressBar: true,
          }).then(() => {
            window.location.href = "/campaign-report"
          })
        }
      } else {
        throw new Error(sendData.error || "Failed to send campaign")
      }
    } catch (error) {
      console.error("Error sending campaign:", error)
      setCampaignSent(false)

      if (typeof window !== "undefined" && window.Swal) {
        window.Swal.fire({
          icon: "error",
          title: "Campaign Send Failed",
          text: error.message || "An unexpected error occurred while sending the campaign.",
          confirmButtonColor: "#ef4444",
        })
      }
    } finally {
      setIsSending(false)
      setIsCreating(false)
    }
  }

  const renderStepIndicator = () => (
    <div className="w-full flex justify-center mb-8">
      <div className="w-full flex items-center justify-between">
        {[1, 2, 3, 4].map((step, index) => (
          <div key={step} className="flex items-center flex-1">
            <div className="flex items-center flex-1">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${currentStep === step
                  ? "bg-blue-600 text-white"
                  : currentStep > step
                    ? "bg-green-600 text-white"
                    : "bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                  }`}
              >
                {currentStep > step ? (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  step
                )}
              </div>
              <div className="ml-3 flex-1">
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  {step === 1 && "Select Template"}
                  {step === 2 && "Configure Variables"}
                  {step === 3 && "Add Recipients"}
                  {step === 4 && "Send Campaign"}
                </div>
              </div>
            </div>
            {index < 3 && (
              <div
                className={`flex-1 h-0.5 mx-4 ${currentStep > step ? "bg-green-600" : "bg-gray-200 dark:bg-gray-700"}`}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  )

  const renderTemplatePreview = () => {
    if (!selectedTemplate) {
      return (
        <div
          className="phone-mockup mx-auto"
          style={{
            width: "240px",
            height: "420px",
            backgroundColor: "#000",
            borderRadius: "28px",
            padding: "3px",
            boxShadow: "0 8px 30px rgba(0,0,0,0.3)",
            position: "relative",
          }}
        >
          <div
            style={{
              width: "100%",
              height: "100%",
              backgroundColor: "#25D366",
              borderRadius: "25px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "0",
                left: "0",
                right: "0",
                height: "32px",
                background: "linear-gradient(180deg, rgba(0,0,0,0.1) 0%, transparent 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "0 16px",
                fontSize: "12px",
                fontWeight: "600",
                color: "white",
              }}
            >
              <span>9:41</span>
              <div style={{ display: "flex", gap: "3px" }}>
                <span>📶</span>
                <span>📶</span>
                <span>🔋</span>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                marginTop: "16px",
              }}
            >
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  backgroundColor: "rgba(255,255,255,0.2)",
                  borderRadius: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "20px",
                  backdropFilter: "blur(10px)",
                }}
              >
                <svg width="50" height="50" viewBox="0 0 24 24" fill="white">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.02-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                </svg>
              </div>
              <h3
                style={{
                  color: "white",
                  marginBottom: "12px",
                  fontSize: "18px",
                  fontWeight: "600",
                  textAlign: "center",
                }}
              >
                Select Template
              </h3>
              <p
                style={{
                  color: "rgba(255,255,255,0.8)",
                  fontSize: "12px",
                  margin: 0,
                  textAlign: "center",
                  maxWidth: "160px",
                  lineHeight: "1.4",
                }}
              >
                Choose a template to see the WhatsApp preview
              </p>
            </div>

            <div
              style={{
                position: "absolute",
                bottom: "6px",
                left: "50%",
                transform: "translateX(-50%)",
                width: "100px",
                height: "4px",
                backgroundColor: "rgba(255,255,255,0.3)",
                borderRadius: "2px",
              }}
            ></div>
          </div>
        </div>
      )
    }

    const analysis = selectedTemplate.parameterAnalysis || {}
    let headerComponent = null
    let bodyComponent = null
    let bodyText = ""

    if (selectedTemplate.components) {
      headerComponent = selectedTemplate.components.find((c) => c.type === "HEADER")
      bodyComponent = selectedTemplate.components.find((c) => c.type === "BODY")

      if (bodyComponent && bodyComponent.text) {
        bodyText = bodyComponent.text

        if (analysis.bodyParameterCount > 0) {
          for (let i = 0; i < analysis.bodyParameterCount; i++) {
            const value = manualVariables[i] || ""
            const placeholder = `{{${i + 1}}}`

            if (value) {
              bodyText = bodyText.replace(placeholder, value)
            } else {
              bodyText = bodyText.replace(placeholder, `[Variable ${i + 1}]`)
            }
          }
        }
      }
    }

    return (
      <div
        className="phone-mockup mx-auto"
        style={{
          width: "240px",
          height: "420px",
          backgroundColor: "#000",
          borderRadius: "28px",
          padding: "3px",
          boxShadow: "0 8px 30px rgba(0,0,0,0.3)",
          position: "relative",
        }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            backgroundColor: "#e5ddd5",
            borderRadius: "25px",
            display: "flex",
            flexDirection: "column",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Header bar */}
          <div
            style={{
              position: "absolute",
              top: "0",
              left: "0",
              right: "0",
              height: "32px",
              backgroundColor: "#075e54",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 16px",
              fontSize: "12px",
              fontWeight: "600",
              color: "white",
              zIndex: 10,
            }}
          >
            <span>9:41</span>
            <div style={{ display: "flex", gap: "3px" }}>
              <span>📶</span>
              <span>📶</span>
              <span>🔋</span>
            </div>
          </div>

          {/* Chat header */}
          <div
            style={{
              backgroundColor: "#075e54",
              padding: "6px 12px",
              marginTop: "32px",
              borderBottom: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <div className="flex items-center">
              <div
                className="bg-yellow-400 rounded-full mr-2 flex items-center justify-center"
                style={{ width: "28px", height: "28px" }}
              >
                <strong style={{ color: "#000", fontSize: "12px" }}>C</strong>
              </div>
              <div>
                <div style={{ fontSize: "14px", fontWeight: "bold", color: "white" }}>
                  {user?.username || "Customer"}
                </div>
                <div style={{ fontSize: "10px", color: "rgba(255,255,255,0.8)" }}>online</div>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, padding: "12px", overflowY: "auto" }}>
            <div
              style={{
                backgroundColor: "#dcf8c6",
                borderRadius: "6px",
                padding: "6px 10px",
                marginBottom: "6px",
                maxWidth: "85%",
                marginLeft: "auto",
                boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                position: "relative",
              }}
            >
              <div className="text-xs text-gray-600 mb-1" style={{ fontSize: "9px", color: "#666" }}>
                {selectedTemplate.name}
              </div>

              {/* ✅ Render media if HEADER has image/video/doc */}
              {headerComponent && headerComponent.format === "IMAGE" && (
                <img
                  src={
                    uploadedMedia?.mediaUrl ||
                    uploadedMedia?.whatsappUrl ||
                    (headerComponent.example?.header_handle?.[0]?.startsWith("http")
                      ? headerComponent.example.header_handle[0]
                      : "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=500&auto=format&fit=crop&q=60")
                  }
                  alt="template header"
                  style={{
                    width: "100%",
                    borderRadius: "6px",
                    marginBottom: "8px",
                    maxHeight: "150px",
                    objectFit: "cover",
                  }}
                />
              )}
              {headerComponent && headerComponent.format === "VIDEO" && (
                <div>
                  {(uploadedMedia?.mediaUrl || uploadedMedia?.whatsappUrl || headerComponent.example?.header_handle?.[0]?.startsWith("http")) ? (
                    <video
                      src={uploadedMedia?.mediaUrl || uploadedMedia?.whatsappUrl || headerComponent.example.header_handle[0]}
                      controls
                      style={{
                        width: "100%",
                        borderRadius: "6px",
                        marginBottom: "8px",
                        maxHeight: "150px",
                        backgroundColor: "#000",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        backgroundColor: "#000",
                        color: "#fff",
                        padding: "8px",
                        borderRadius: "6px",
                        marginBottom: "8px",
                        textAlign: "center",
                        fontSize: "10px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100px",
                      }}
                    >
                      <span className="text-xl mb-1">🎥</span>
                      <span>Video Template Header</span>
                      <span className="text-[8px] text-gray-400 mt-1">(Upload a video to preview)</span>
                    </div>
                  )}
                </div>
              )}
              {headerComponent && headerComponent.format === "DOCUMENT" && (
                <div
                  style={{
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #dee2e6",
                    color: "#333",
                    padding: "8px 12px",
                    borderRadius: "6px",
                    marginBottom: "8px",
                    fontSize: "10px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span className="text-xl">📄</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: "600", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                      {uploadedMedia?.fileName || "document.pdf"}
                    </div>
                    <div style={{ fontSize: "8px", color: "#6c757d" }}>
                      {uploadedMedia ? "Uploaded Document" : "PDF Template Header"}
                    </div>
                  </div>
                </div>
              )}

              {/* Body text */}
              {bodyText && <div style={{ fontSize: "12px", color: "#000", lineHeight: "1.4" }}>{bodyText}</div>}

              <div
                style={{
                  textAlign: "right",
                  fontSize: "9px",
                  color: "#666",
                  marginTop: "3px",
                }}
              >
                10:51 AM ✓✓
              </div>

              <div
                style={{
                  position: "absolute",
                  right: "-5px",
                  bottom: "6px",
                  width: "0",
                  height: "0",
                  borderLeft: "5px solid #dcf8c6",
                  borderTop: "5px solid transparent",
                  borderBottom: "5px solid transparent",
                }}
              ></div>
            </div>
          </div>

          {/* Input box */}
          <div
            style={{
              backgroundColor: "#f0f0f0",
              padding: "6px 12px",
              borderTop: "1px solid #ddd",
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "16px",
                padding: "6px 12px",
                fontSize: "12px",
                color: "#999",
              }}
            >
              Type a message...
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderTemplateSelection = () => (
    <div className="w-full mx-auto bg-gray-50 dark:bg-gray-900 bg-gradient-to-br from-green-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
      {/* Header with balance */}
      <div className="w-full flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6">
        <div className="w-full">{renderStepIndicator()}</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="md:col-span-8 col-md-8 min-w-0">
          <div className="flex flex-wrap justify-between items-start xl:items-center mb-6 gap-4">
            <div>
              <h1 className="text-lg font-bold text-gray-900 dark:text-white mb-1">Campaign -</h1>
              <div className="text-md font-bold text-green-600 dark:text-green-400">
                Credit : ₹{userBalance.toFixed(2)}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 shrink">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search templates..."
                  value={templateSearchQuery}
                  onChange={(e) => setTemplateSearchQuery(e.target.value)}
                  className="w-64 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <svg
                  className="absolute right-3 top-2.5 h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>

              <div className="relative">
                <div className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white flex items-center space-x-2 shadow-sm">
                  <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Daily Limit:</span>
                  <span className="font-semibold text-sm text-blue-600 dark:text-blue-400">
                    {loadingLimit ? (
                      <div className="animate-pulse h-4 w-12 bg-blue-200 dark:bg-blue-800 rounded"></div>
                    ) : (
                      maxLimit === Infinity ? "Unlimited" : (maxLimit !== null ? maxLimit : (user?.wabaLimit || "Not set"))
                    )}
                  </span>
                </div>
              </div>

              <div className="relative">
                <div className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white flex items-center space-x-2 shadow-sm">
                  <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Current Limit:</span>
                  <span className="font-semibold text-sm text-orange-600 dark:text-orange-400">
                    {loadingLimit ? (
                      <div className="animate-pulse h-4 w-12 bg-orange-200 dark:bg-orange-800 rounded"></div>
                    ) : (
                      currentLimit !== null ? currentLimit : (user?.wabaLimit || "Not set")
                    )}
                  </span>
                </div>
              </div>

              <div className="relative">
                <div className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white flex items-center space-x-2 shadow-sm">
                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Default Sender:</span>
                  <span className="font-semibold text-sm text-green-700 dark:text-green-400">
                    {selectedPhoneNumber?.display_phone_number || availableSenderNumbers?.[0]?.display_phone_number || user?.cloudNumber || user?.number || "Primary Number"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Updated template selection header */}

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="h-96 overflow-y-auto overflow-x-hidden pr-2">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {filteredTemplates.map((template) => {
                  const analysis = template.parameterAnalysis || {}
                  return (
                    <div
                      key={template.id}
                      className={`p-6 rounded-xl border-2 cursor-pointer transition-all duration-200 shadow-sm hover:shadow-md ${selectedTemplate?.id === template.id
                        ? "border-blue-600 bg-blue-50 dark:bg-blue-900/20 shadow-lg"
                        : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                        } bg-white dark:bg-gray-800`}
                      onClick={() => setSelectedTemplate(template)}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{template.name}</h3>
                        <span
                          className={`px-3 py-1 text-xs rounded-full font-medium ${template.status.toLowerCase() === "approved"
                            ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
                            : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400"
                            }`}
                        >
                          {template.status}
                        </span>
                      </div>

                      <div className="flex gap-2 mb-4">
                        <span className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md font-medium">
                          {template.language || "EN"}
                        </span>
                        <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded-md font-medium">
                          {template.category}
                        </span>
                      </div>

                      {template.components && (
                        <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                          <strong>Components:</strong> {template.components.map((c) => c.type).join(", ")}
                        </div>
                      )}

                      {(analysis.headerRequiresParameters || analysis.bodyParameterCount > 0) && (
                        <div className="text-sm">
                          <strong className="text-green-600 dark:text-green-400">Parameters:</strong>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {analysis.headerRequiresParameters && (
                              <span className="px-2 py-1 text-xs bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400 rounded-md">
                                HEADER: 1
                              </span>
                            )}
                            {analysis.bodyParameterCount > 0 && (
                              <span className="px-2 py-1 text-xs bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 rounded-md">
                                BODY: {analysis.bodyParameterCount}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        <div className="md:col-span-4 col-md-4">
          <div className="sticky top-6">
            <div className="rounded-lg border-gray-200 dark:border-gray-700 p-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M7 2a2 2 0 00-2 2v12a2 2 0 002 2h6a2 2 0 002-2V4a2 2 0 00-2-2H7zM6 4a1 1 0 011-1h6a1 1 0 011 1v12a1 1 0 01-1 1H7a1 1 0 01-1-1V4z"
                    clipRule="evenodd"
                  />
                </svg>
                WhatsApp Preview
              </h3>
              {renderTemplatePreview()}
            </div>
            {selectedTemplate && (
              <div className="text-center">
                <button
                  onClick={() => setCurrentStep(2)}
                  disabled={
                    (user?.operator !== "karix" && !selectedTemplate)
                  }
                  className="my-4 px-4 py-2 bg-gradient-to-r from-green-600 to-yellow-600 text-white font-semibold rounded-xl disabled:opacity-50"
                >
                  {!selectedPhoneNumber
                    ? "Select Phone Number"
                    : user?.operator === "karix"
                      ? "Continue →"
                      : "Continue →"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {showPhoneNumberModal && selectedWabaAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 max-h-96 overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Select Phone Number for campaign</h3>
              <button
                onClick={() => setShowPhoneNumberModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-2">
              {selectedWabaAccount.phoneNumbers?.map((phoneNumber) => (
                <button
                  key={phoneNumber.id}
                  onClick={() => handlePhoneNumberSelect(phoneNumber)}
                  className={`w-full p-3 text-left border rounded-lg transition-colors ${selectedPhoneNumber?.id === phoneNumber.id
                    ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                    : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
                    }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {phoneNumber.display_phone_number}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {phoneNumber.verified_name || "Unnamed"}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {phoneNumber.quality_rating === "GREEN" && (
                        <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">High Quality</span>
                      )}
                      {phoneNumber.status === "CONNECTED" && (
                        <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">Connected</span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowPhoneNumberModal(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )

  const renderVariableSetup = () => {
    const analysis = selectedTemplate?.parameterAnalysis || {}
    const bodyParameterCount = analysis.bodyParameterCount || 0
    const headerParameterCount = analysis.headerParameterCount || 0
    const totalParameters = bodyParameterCount + headerParameterCount

    return (
      <div className="w-full mx-auto bg-gray-50 dark:bg-gray-900 bg-gradient-to-br from-green-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
        {/* Step Indicator */}
        <div className="w-full flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6">
          <div className="w-full">{renderStepIndicator()}</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {/* Left Content */}
          <div className="md:col-span-8 col-md-8">
            <div className="text-left mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Configure Variables & Media</h2>
              <p className="text-gray-600 dark:text-gray-400">Set up your campaign content and variables</p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Campaign Details</h3>
                <div className="flex items-center space-x-2 bg-blue-50 dark:bg-blue-900/20 px-3 py-1.5 rounded-lg border border-blue-100 dark:border-blue-800">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Current Limit:</span>
                  <span className="font-bold text-blue-600 dark:text-blue-400">
                    {loadingLimit ? "..." : (currentLimit !== null ? currentLimit : "Loading...")}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Campaign Name *
                </label>
                <input
                  type="text"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Enter campaign name"
                  required
                  disabled={campaignCreated}
                />
                {campaignName.trim() === "" && campaignCreated === false && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">Campaign name is required to proceed</p>
                )}
              </div>

              {campaignName.trim() && !campaignCreated && (
                <div className="mt-4">
                  <button
                    onClick={createCampaign}
                    disabled={isCreating}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isCreating && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                    {isCreating ? "Creating Campaign..." : "Create Campaign"}
                  </button>
                </div>
              )}

              {campaignCreated && (
                <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 text-green-600 dark:text-green-400 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-green-700 dark:text-green-400 font-medium">
                      Campaign created successfully! You can now upload media and configure variables.
                    </span>
                  </div>
                </div>
              )}
            </div>

            {campaignCreated && (
              <>
                {/* Karix operator → Only Media URL input (no upload) */}
                {user?.operator === "karix" && getHeaderMediaType() && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Media {getHeaderMediaType() === "document" ? "Document" : "Upload"}
                    </h3>

                    {/* File upload option */}
                    <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center mb-4">
                      <input
                        type="file"
                        accept={
                          getHeaderMediaType() === "image"
                            ? "image/*"
                            : getHeaderMediaType() === "video"
                              ? "video/*"
                              : ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                        }
                        onChange={handleMediaUpload}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                        Upload {getHeaderMediaType()?.toUpperCase()} file (will be hosted and URL generated
                        automatically)
                      </p>
                    </div>

                    {/* OR divider */}
                    <div className="relative my-4">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-white dark:bg-gray-800 text-gray-500">OR</span>
                      </div>
                    </div>

                    {/* URL input option */}
                    <input
                      type="text"
                      placeholder="https://example.com/file.jpg"
                      value={uploadedMedia?.mediaUrl || ""}
                      onChange={(e) =>
                        setUploadedMedia({
                          ...(uploadedMedia || {}),
                          mediaUrl: e.target.value,
                          localPreviewUrl: e.target.value,
                          ...(getHeaderMediaType() === "document"
                            ? {
                              fileName: e.target.value.split("/").pop().split("?")[0] || "document.pdf",
                            }
                            : {}),
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      Or paste a public URL for {getHeaderMediaType()?.toUpperCase()}
                      {getHeaderMediaType() === "document" &&
                        " (fileName will be auto-extracted from URL)"}
                    </p>

                    {uploadedMedia?.mediaUrl && (
                      <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                        <span className="text-green-700 dark:text-green-400 font-medium">
                          ✓ Media ready: {uploadedMedia.fileName || uploadedMedia.mediaUrl}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Cloud operator → Upload OR URL (both allowed) */}
                {["cloud", "pinacle"].includes(user?.operator) && getHeaderMediaType() && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Upload Media / Use URL</h3>

                    {/* Upload option */}
                    <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                      <input
                        type="file"
                        accept={
                          getHeaderMediaType() === "image"
                            ? "image/*"
                            : getHeaderMediaType() === "video"
                              ? "video/*"
                              : ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                        }
                        onChange={handleMediaUpload}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                    </div>

                    {/* OR URL input */}
                    <div className="mt-4">
                      <input
                        type="text"
                        placeholder="Optional: https://example.com/file.jpg"
                        value={uploadedMedia?.mediaUrl || ""}
                        onChange={(e) => setUploadedMedia({ ...(uploadedMedia || {}), mediaUrl: e.target.value, localPreviewUrl: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                        You can either upload a file (preferred) or paste a direct media URL.
                      </p>
                    </div>

                    {uploadedMedia?.mediaId && (
                      <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                        <span className="text-green-700 dark:text-green-400 font-medium">
                          Uploaded successfully (Media ID: {uploadedMedia.mediaId})
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Variable Configuration */}
                {totalParameters > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Variable Configuration</h3>

                    <div className="mb-6">
                      <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">Choose Input Method</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${inputMethod === "manual"
                            ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                            : "border-gray-300 dark:border-gray-600 hover:border-gray-400"
                            }`}
                          onClick={() => {
                            setInputMethod("manual")
                            setShowColumnMapping(false)
                            setExcelMappingConfigured(false)
                          }}
                        >
                          <div className="flex items-center mb-2">
                            <svg
                              className="w-5 h-5 text-blue-600 mr-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                              />
                            </svg>
                            <span className="font-medium text-gray-900 dark:text-white">Manual Input</span>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Enter default values manually</p>
                        </div>

                        <div
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${inputMethod === "excel"
                            ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                            : "border-gray-300 dark:border-gray-600 hover:border-gray-400"
                            }`}
                          onClick={() => {
                            setInputMethod("excel")
                            setManualVariables({})
                          }}
                        >
                          <div className="flex items-center mb-2">
                            <svg
                              className="w-5 h-5 text-green-600 mr-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                              />
                            </svg>
                            <span className="font-medium text-gray-900 dark:text-white">Excel Upload</span>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Upload Excel with phone numbers and variables
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Excel Upload Section */}
                    {inputMethod === "excel" && (
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                        <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                          Excel Upload with Variables
                        </h4>
                        <p className="text-sm text-blue-700 dark:text-blue-300 mb-4">
                          Upload Excel with columns: phone (required), and variable columns matching your template
                        </p>

                        <div className="flex items-center space-x-4">
                          <input
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleVariableExcelUpload}
                            className="hidden"
                            id="variable-excel-upload"
                          />
                          <label
                            htmlFor="variable-excel-upload"
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg cursor-pointer transition-colors"
                          >
                            Choose File
                          </label>
                          {uploadedVariableFile && (
                            <span className="text-sm text-green-600 dark:text-green-400">
                              ✓ Uploaded: {uploadedVariableFile}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {inputMethod === "excel" && showColumnMapping && excelColumns.length > 0 && (
                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 mb-6">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Variable Mapping</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                          Map your Excel columns to template variables. Found {totalRecords} records.
                        </p>

                        <div className="space-y-4">
                          {/* Phone Column Mapping */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Phone Column *
                              </label>
                              <select
                                value={phoneColumn}
                                onChange={(e) => setPhoneColumn(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              >
                                <option value="">Select phone column</option>
                                {excelColumns.map((col) => (
                                  <option key={col} value={col}>
                                    {col}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          {/* Body Variables Mapping */}
                          {bodyParameterCount > 0 && (
                            <div>
                              <h5 className="text-md font-medium text-gray-900 dark:text-white mb-3">Body Variables</h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {Array.from({ length: bodyParameterCount }, (_, index) => (
                                  <div key={`body-var-${index}`}>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                      Variable {index + 1} *
                                    </label>
                                    <select
                                      value={variableMapping[`var${index + 1}`] || ""}
                                      onChange={(e) =>
                                        setVariableMapping((prev) => ({
                                          ...prev,
                                          [`var${index + 1}`]: e.target.value,
                                        }))
                                      }
                                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    >
                                      <option value="">Select column for Variable {index + 1}</option>
                                      {excelColumns
                                        .filter((col) => col !== phoneColumn) // Exclude phone column from variable options
                                        .map((col) => (
                                          <option key={col} value={col}>
                                            {col}
                                          </option>
                                        ))}
                                    </select>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Header Variables Mapping */}
                          {headerParameterCount > 0 && (
                            <div>
                              <h5 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                                Header Variables
                              </h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {Array.from({ length: headerParameterCount }, (_, index) => (
                                  <div key={`header-var-${index}`}>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                      Header Variable {index + 1} *
                                    </label>
                                    <select
                                      value={variableMapping[`header_var${index + 1}`] || ""}
                                      onChange={(e) =>
                                        setVariableMapping((prev) => ({
                                          ...prev,
                                          [`header_var${index + 1}`]: e.target.value,
                                        }))
                                      }
                                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    >
                                      <option value="">Select column for Header Variable {index + 1}</option>
                                      {excelColumns
                                        .filter((col) => col !== phoneColumn) // Exclude phone column from variable options
                                        .map((col) => (
                                          <option key={col} value={col}>
                                            {col}
                                          </option>
                                        ))}
                                    </select>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="flex justify-end">
                            <button
                              onClick={handleSaveColumnMapping}
                              disabled={!phoneColumn}
                              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Save Mapping
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Manual Variable Input */}
                    {inputMethod === "manual" && bodyParameterCount > 0 && (
                      <div className="space-y-4">
                        <h4 className="text-md font-medium text-gray-900 dark:text-white">Default Variable Values</h4>
                        {Array.from({ length: bodyParameterCount }, (_, i) => (
                          <div key={i}>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Variable {i + 1}
                            </label>
                            <input
                              type="text"
                              value={manualVariables[i] || ""}
                              onChange={(e) =>
                                setManualVariables((prev) => ({
                                  ...prev,
                                  [i]: e.target.value,
                                }))
                              }
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              placeholder={`Enter default value for variable ${i + 1}`}
                            />
                          </div>
                        ))}
                        <div className="mt-4 flex flex-wrap items-center gap-3">
                          {campaignCreated && (inputMethod === "manual" || phoneInputMethod === "manual") && (
                            <button
                              onClick={saveManualVariables}
                              disabled={isSending || isCreating}
                              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Save Variables
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            <div className="flex justify-between">
              <button
                onClick={() => setCurrentStep(1)}
                className="bg-gray-600 text-white px-6 py-2 rounded-xl hover:bg-gray-700 transition-colors"
              >
                ← Previous
              </button>

              <button
                onClick={() => {
                  if (inputMethod === "excel" && bodyParameterCount > 0 && !excelMappingConfigured) {
                    if (typeof window !== "undefined" && window.Swal) {
                      window.Swal.fire({
                        icon: "warning",
                        title: "Mapping Required",
                        text: "Please configure variable mapping before proceeding.",
                        confirmButtonColor: "#f59e0b",
                      })
                    }
                    return
                  }
                  // Default the recipients step to show the Excel tab if we have mapping/data

                  if (excelMappingConfigured || (Array.isArray(excelData) && excelData.length > 0)) {

                    setPhoneInputMethod("excel")

                  }
                  setCurrentStep(3)
                }}
                disabled={!canProceedToNextStep()}
                className="bg-blue-600 text-white px-6 py-2 rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next: Add Recipients →
              </button>
            </div>
          </div>

          <div className="md:col-span-4 col-md-4">
            <div className="sticky top-6">
              <div className="rounded-lg border-gray-200 dark:border-gray-700 p-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M7 2a2 2 0 00-2 2v12a2 2 0 002 2h6a2 2 0 002-2V4a2 2 0 00-2-2H7zM6 4a1 1 0 011-1h6a1 1 0 011 1v12a1 1 0 01-1 1H7a1 1 0 01-1-1V4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  WhatsApp Preview
                </h3>
                {renderTemplatePreview()}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderPhoneSetup = () => {

    // ✅ Detect if Excel mapping or upload already exists
    const excelReady = excelMappingConfigured || (Array.isArray(excelData) && excelData.length > 0);

    // ✅ Auto-switch to Excel mode if mapping already done

    return (
      <div className="mx-auto bg-gray-50 dark:bg-gray-900 bg-gradient-to-br from-green-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
        <div className="w-full flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6">
          <div className="w-full">{renderStepIndicator()}</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-8 space-y-6 col-md-8">
            {/* Header */}
            <div className="text-left">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Add Recipients</h2>
              <p className="text-gray-600 dark:text-gray-400">
                Choose how to add phone numbers for your campaign
              </p>
            </div>

            {/* ✅ Only show selector if Excel not already uploaded */}
            {!excelReady && (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Choose Phone Input Method
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${phoneInputMethod === "manual"
                      ? "border-green-600 bg-green-50 dark:bg-green-900/20"
                      : "border-gray-200 dark:border-gray-700 hover:border-gray-300"
                      }`}
                    onClick={() => setPhoneInputMethod("manual")}
                  >
                    <div className="flex items-center mb-2">
                      <svg
                        className="w-5 h-5 mr-2 text-green-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                      <span className="font-medium text-gray-900 dark:text-white">Manual Entry</span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Enter phone numbers manually
                    </p>
                  </div>

                  <div
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${phoneInputMethod === "excel"
                      ? "border-orange-600 bg-orange-50 dark:bg-orange-900/20"
                      : "border-gray-200 dark:border-gray-700 hover:border-gray-300"
                      }`}
                    onClick={() => setPhoneInputMethod("excel")}
                  >
                    <div className="flex items-center mb-2">
                      <svg
                        className="w-5 h-5 mr-2 text-orange-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="font-medium text-gray-900 dark:text-white">Excel Upload</span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Upload Excel file with phone numbers
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ✅ Phone Input Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              {excelReady ? (
                /* ✅ CASE 1: Excel mapped → show preview only */
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Uploaded Excel Preview
                  </h3>
                  {Array.isArray(excelData) && excelData.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm border border-gray-200 dark:border-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900/30">
                          <tr>
                            {Object.keys(excelData[0] || {}).map((col) => (
                              <th
                                key={col}
                                className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-300 border-b"
                              >
                                {col}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {excelData.slice(0, 3).map((row, idx) => (
                            <tr
                              key={idx}
                              className="border-b border-gray-100 dark:border-gray-700"
                            >
                              {Object.keys(row).map((col) => (
                                <td
                                  key={col}
                                  className="px-3 py-2 text-gray-800 dark:text-gray-200"
                                >
                                  {String(row[col] ?? "")}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {excelMappingConfigured && phoneColumn && (
                        <p className="mt-3 text-xs text-gray-600 dark:text-gray-400">
                          Using mapped column “{phoneColumn}”. Total records:{" "}
                          {totalRecords || recipientCount}.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                /* ✅ CASE 2: Manual entry or new Excel upload */
                <>
                  {phoneInputMethod === "manual" ? (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        Manual Phone Numbers
                      </h3>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Enter phone numbers (one per line)
                      </label>
                      <textarea
                        value={phoneNumbers}
                        onChange={(e) => setPhoneNumbers(e.target.value)}
                        rows={10}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                        placeholder="919876543210&#10;919876543211&#10;919876543212"
                      />
                      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        Enter phone numbers with country code (91 for India)
                      </p>
                    </div>
                  ) : (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        Excel File Upload
                      </h3>
                      <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
                        <svg
                          className="mx-auto h-12 w-12 text-gray-400 mb-4"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 48 48"
                        >
                          <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                          Upload Excel file with phone numbers
                        </h4>
                        <p className="text-gray-600 dark:text-gray-400 mb-4">
                          Excel should have a 'phone' column with numbers
                        </p>
                        <input
                          type="file"
                          accept=".xlsx,.xls,.csv"
                          onChange={handlePhoneExcelUpload}
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                        />
                      </div>
                      {Array.isArray(excelData) && excelData.length > 0 && (
                        <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                          <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-3">
                            Sample from Excel (first 3 rows)
                          </h4>
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-sm border border-gray-200 dark:border-gray-700">
                              <thead>
                                <tr className="bg-gray-50 dark:bg-gray-900/30">
                                  {Object.keys(excelData[0] || {}).map((h) => (
                                    <th
                                      key={h}
                                      className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-300 border-b"
                                    >
                                      {h}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {excelData.slice(0, 3).map((row, idx) => (
                                  <tr
                                    key={idx}
                                    className="border-b border-gray-100 dark:border-gray-700"
                                  >
                                    {Object.keys(row).map((h) => (
                                      <td
                                        key={h}
                                        className="px-3 py-2 text-gray-800 dark:text-gray-200"
                                      >
                                        {String(row[h] ?? "")}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Recipient summary + buttons remain unchanged */}
            {recipientCount > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                    Recipients Summary
                  </h4>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <div>
                      <strong>Valid Numbers:</strong> {recipientCount}
                    </div>
                  </div>
                </div>

              </div>
            )}

            {balanceValidation && (
              <div
                className={`p-4 rounded-lg ${balanceValidation.status === "success"
                  ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400"
                  : "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400"
                  }`}
              >
                {balanceValidation.message}
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between">
              <button
                onClick={() => setCurrentStep(2)}
                className="bg-gray-600 text-white px-6 py-2 rounded-xl hover:bg-gray-700 transition-colors"
              >
                ← Previous
              </button>
              <button
                onClick={() => setCurrentStep(4)}
                disabled={!canProceedToNextStep()}
                className="text-white px-6 py-2 rounded-lg bg-gradient-to-r from-green-600 to-yellow-600 text-white font-semibold rounded-xl hover:from-green-700 hover:to-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next: Review & Send →
              </button>
            </div>
          </div>

          {/* Right-side WhatsApp Preview */}
          <div className="md:col-span-4 col-md-4">
            <div className="sticky top-6">
              <div className="rounded-lg border-gray-200 dark:border-gray-700 p-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M7 2a2 2 0 00-2 2v12a2 2 0 002 2h6a2 2 0 002-2V4a2 2 0 00-2-2H7zM6 4a1 1 0 011-1h6a1 1 0 011 1v12a1 1 0 01-1 1H7a1 1 0 01-1-1V4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  WhatsApp Preview
                </h3>
                {renderTemplatePreview()}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderReviewAndSend = () => (
    <div className="w-full mx-auto">
      <div className="w-full mb-6">{renderStepIndicator()}</div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="md:col-span-8 space-y-6 col-md-8">
          {/* Campaign Review */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Campaign Details</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Name:</span>
                    <span className="font-medium">{campaignName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Template:</span>
                    <span className="font-medium">{selectedTemplate?.name}</span>
                  </div>

                  {selectedPhoneNumber && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Phone Number:</span>
                      <span className="font-medium">{selectedPhoneNumber.display_phone_number}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Daily Limit:</span>
                    <span className="font-medium text-green-600 dark:text-green-400">
                      {loadingLimit ? "Loading..." : (maxLimit === null ? "Not set" : maxLimit === Infinity ? "Unlimited" : maxLimit.toLocaleString())}
                    </span>
                  </div>

                </div>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Balance</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Current Balance:</span>
                    <span className="font-medium">₹{userBalance.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">WABA Account:</span>
                    <span className="font-medium">
                      {selectedWabaAccount?.name || `WABA ${selectedWabaAccount?.id?.slice(-6)}`}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Recipients:</span>
                    <span className="font-medium">{recipientCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Current Limit:</span>
                    <span className={`font-medium ${
                      loadingLimit ? "text-gray-400" :
                      currentLimit === null ? "text-gray-400" :
                      currentLimit === 0 ? "text-red-600 dark:text-red-400" :
                      currentLimit < recipientCount ? "text-orange-500 dark:text-orange-400" :
                      "text-green-600 dark:text-green-400"
                    }`}>
                      {loadingLimit ? "Loading..." : (currentLimit !== null ? currentLimit.toLocaleString() : "Not set")}
                    </span>
                  </div>
                  {!loadingLimit && limitResetOn && currentLimit !== null && currentLimit < recipientCount && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Resets On:</span>
                      <span className="font-medium text-orange-500 dark:text-orange-400">{limitResetOn}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {Object.keys(manualVariables).length > 0 && (
              <div className="mt-6">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Template Variables</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  {Object.entries(manualVariables).map(([key, value]) => (
                    <div key={key} className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Variable {Number.parseInt(key) + 1}:</span>
                      <span className="font-medium">{value || "Not set"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sender Number Selection Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Select Sender Number</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Choose which WhatsApp sender number and credentials to broadcast this campaign from. By default, your primary number is selected.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {availableSenderNumbers.map((phone) => {
                const isSelected = selectedPhoneNumber && (selectedPhoneNumber.id === phone.id || selectedPhoneNumber.phoneNumberId === phone.phoneNumberId || selectedPhoneNumber.display_phone_number === phone.display_phone_number)
                return (
                  <div
                    key={phone.id || phone.display_phone_number}
                    onClick={() => handleSenderNumberConfirmSelect(phone)}
                    className={`p-4 rounded-xl border cursor-pointer transition-all duration-200 flex items-center justify-between ${isSelected
                      ? "border-green-500 bg-green-50 dark:bg-green-900/20 shadow-sm"
                      : "border-gray-200 dark:border-gray-700 hover:border-green-300 dark:hover:border-green-600 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                      }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isSelected ? "bg-green-600 text-white" : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                        }`}>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                          <span>{phone.display_phone_number}</span>
                          {phone.isPrimary && (
                            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 rounded-full font-medium">
                              Primary
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {phone.verified_name || "WhatsApp Sender"}
                        </div>
                      </div>
                    </div>
                    <div>
                      {isSelected ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300">
                          ✓ Selected
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400 dark:text-gray-500 font-medium">Click to select</span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Send Settings */}
          {!campaignSent && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Send Settings</h3>
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    type="button"
                    onClick={() => setScheduleOption("immediate")}
                    className={`flex-1 py-3 px-4 rounded-xl border font-semibold text-sm transition-all flex items-center justify-center gap-2 ${scheduleOption === "immediate"
                      ? "bg-green-50 dark:bg-green-900/20 border-green-500 text-green-700 dark:text-green-400"
                      : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                      }`}
                  >
                    🚀 Send Immediately
                  </button>
                  <button
                    type="button"
                    onClick={() => setScheduleOption("later")}
                    className={`flex-1 py-3 px-4 rounded-xl border font-semibold text-sm transition-all flex items-center justify-center gap-2 ${scheduleOption === "later"
                      ? "bg-blue-50 dark:bg-blue-900/20 border-blue-500 text-blue-700 dark:text-blue-400"
                      : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                      }`}
                  >
                    📅 Schedule for Later
                  </button>
                </div>

                {scheduleOption === "later" && (
                  <div className="space-y-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Select Date and Time
                    </label>
                    <input
                      type="datetime-local"
                      value={scheduleTime}
                      onChange={(e) => setScheduleTime(e.target.value)}
                      min={(() => {
                        const d = new Date(Date.now() + 60000)
                        const offsetMs = d.getTimezoneOffset() * 60 * 1000
                        const localDate = new Date(d.getTime() - offsetMs)
                        return localDate.toISOString().slice(0, 16)
                      })()}
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                    />
                    {new Date(scheduleTime) <= new Date() && (
                      <p className="text-xs text-red-600 dark:text-red-400 font-medium">
                        ⚠️ Please select a date and time in the future.
                      </p>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Note: The campaign will be saved in the database and processed automatically at the scheduled time.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Campaign Report */}
          {showReport && campaignReport && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                Campaign Report
                {campaignReport.queueStatus === "processing" && (
                  <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full animate-pulse">
                    Processing...
                  </span>
                )}
                {campaignReport.queueStatus === "completed" && (
                  <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">Completed</span>
                )}
                {campaignReport.queueStatus === "scheduled" && (
                  <span className="ml-2 px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                    Scheduled
                  </span>
                )}
              </h3>

              {campaignReport.queueStatus === "scheduled" && (
                <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 rounded-xl">
                  📅 This campaign is scheduled to run at{" "}
                  <strong>{new Date(campaignReport.scheduledAt).toLocaleString()}</strong>.
                </div>
              )}

              {campaignReport.queueStatus === "processing" && campaignReport.progress !== undefined && (
                <div className="mb-4">
                  <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                    <span>Progress</span>
                    <span>{campaignReport.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                    <div
                      className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
                      style={{ width: `${campaignReport.progress}%` }}
                    ></div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {campaignReport.stats?.sent || campaignReport.messagesSent || 0}
                  </div>
                  <div className="text-sm text-green-700 dark:text-green-300">Messages Sent</div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 capitalize">
                    {campaignReport.queueStatus}
                  </div>
                  <div className="text-sm text-blue-700 dark:text-blue-300">Queue Status</div>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    {campaignReport.campaignId?.slice(-6)}
                  </div>
                  <div className="text-sm text-purple-700 dark:text-purple-300">Campaign ID</div>
                </div>
                <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-orange-600 dark:text-orange-400 capitalize">
                    {campaignReport.operator || "Cloud"}
                  </div>
                  <div className="text-sm text-orange-700 dark:text-orange-300">Operator</div>
                </div>
              </div>
              <div className="mt-4 flex justify-between items-center">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Sent at: {new Date(campaignReport.timestamp).toLocaleString()}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Via: {campaignReport.sendVia || "Panel"}</div>
              </div>
            </div>
          )}

          <div className="flex justify-between">
            <button
              onClick={() => setCurrentStep(3)}
              disabled={isSending || isCreating}
              className="bg-gray-600 text-white px-6 py-2 rounded-xl hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ← Previous
            </button>

            <div className="flex gap-3">
              {campaignSent && (
                <button
                  onClick={() => {
                    setCampaignId(null)
                    setCampaignSent(false)
                    setSendAttempts(0)
                    setShowReport(false)
                    setCampaignReport(null)
                    setCurrentStep(1)
                    setSelectedTemplate(null)
                    setCampaignName("")
                    setPhoneNumbers("")
                    setRecipientCount(0)
                    setManualVariables({})
                    setUploadedMedia(null)
                    setSelectedWabaAccount(null) // Reset WABA account
                    setSelectedPhoneNumber(null) // Reset phone number
                    setInputMethod("manual") // Reset input method
                    setPhoneInputMethod("manual") // Reset phone input method
                    setExcelData([])
                    setExcelColumns([])
                    setPhoneColumn("")
                    setVariableMapping({})
                    setUploadedVariableFile(null)
                    setShowColumnMapping(false)
                    setExcelMappingConfigured(false)
                    setTotalRecords(0)
                    setScheduleOption("immediate")
                    const d = new Date()
                    d.setHours(d.getHours() + 1)
                    const offsetMs = d.getTimezoneOffset() * 60 * 1000
                    const localDate = new Date(d.getTime() - offsetMs)
                    setScheduleTime(localDate.toISOString().slice(0, 16))
                  }}
                  className="bg-gray-600 text-white px-6 py-2 rounded-xl hover:bg-gray-700 transition-colors"
                >
                  New Campaign
                </button>
              )}

              <button
                onClick={sendCampaign}
                disabled={isSending || isCreating || !canProceed || (scheduleOption === "later" && new Date(scheduleTime) <= new Date())}
                className={`px-6 py-2 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium ${campaignSent
                  ? "bg-orange-600 hover:bg-orange-700 text-white"
                  : "bg-green-600 hover:bg-green-700 text-white"
                  }`}
              >
                {(isSending || isCreating) && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                {isSending
                  ? "Sending Campaign..."
                  : isCreating
                    ? "Creating Campaign..."
                    : campaignSent
                      ? "Resend Campaign"
                      : "Send Campaign"}
              </button>
            </div>
          </div>
        </div>

        <div className="md:col-span-4 col-md-4">
          <div className="sticky top-6">
            <div className="rounded-lg border-gray-200 dark:border-gray-700 p-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M7 2a2 2 0 00-2 2v12a2 2 0 002 2h6a2 2 0 002-2V4a2 2 0 00-2-2H7zM6 4a1 1 0 011-1h6a1 1 0 011 1v12a1 1 0 01-1 1H7a1 1 0 01-1-1V4z"
                    clipRule="evenodd"
                  />
                </svg>
                WhatsApp Preview
              </h3>
              {renderTemplatePreview()}
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className="bg-gray-50 dark:bg-gray-900 py-8 bg-gray-50 dark:bg-gray-900 bg-gradient-to-br from-green-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
        {currentStep === 1 && renderTemplateSelection()}
        {currentStep === 2 && renderVariableSetup()}
        {currentStep === 3 && renderPhoneSetup()}
        {currentStep === 4 && renderReviewAndSend()}
      </div>
    </div>
  )
}

export default Campaign